/**
 * Error Handler Hook for Store-First Mobile
 * 
 * Provides error handling utilities with categorization and user-friendly messages
 */

import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export type ErrorCategory = 
  | 'network'
  | 'validation'
  | 'authentication'
  | 'not_found'
  | 'conflict'
  | 'server'
  | 'unknown';

export interface ErrorInfo {
  message: string;
  category: ErrorCategory;
  code?: string;
  retryable: boolean;
}

/**
 * Categorize error based on response or error type
 */
function categorizeError(error: unknown): ErrorInfo {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    // Network errors
    if (message.includes('network') || message.includes('fetch') || message.includes('failed to fetch')) {
      return {
        message: 'Network connection failed. Please check your internet connection.',
        category: 'network',
        retryable: true,
      };
    }
    
    // Authentication errors
    if (message.includes('unauthorized') || message.includes('401') || message.includes('authentication')) {
      return {
        message: 'Authentication failed. Please log in again.',
        category: 'authentication',
        code: 'AUTH_ERROR',
        retryable: false,
      };
    }
    
    // Not found errors
    if (message.includes('not found') || message.includes('404')) {
      return {
        message: 'Resource not found.',
        category: 'not_found',
        code: 'NOT_FOUND',
        retryable: false,
      };
    }
    
    // Validation errors
    if (message.includes('validation') || message.includes('invalid') || message.includes('400')) {
      return {
        message: error.message,
        category: 'validation',
        code: 'VALIDATION_ERROR',
        retryable: false,
      };
    }
    
    // Conflict errors
    if (message.includes('conflict') || message.includes('409')) {
      return {
        message: 'This operation conflicts with existing data. Please refresh and try again.',
        category: 'conflict',
        code: 'CONFLICT',
        retryable: true,
      };
    }
    
    // Server errors
    if (message.includes('500') || message.includes('server')) {
      return {
        message: 'Server error occurred. Please try again later.',
        category: 'server',
        code: 'SERVER_ERROR',
        retryable: true,
      };
    }
    
    // Generic error
    return {
      message: error.message,
      category: 'unknown',
      retryable: false,
    };
  }
  
  // Unknown error type
  return {
    message: 'An unexpected error occurred',
    category: 'unknown',
    retryable: false,
  };
}

interface UseErrorHandlerReturn {
  error: ErrorInfo | null;
  handleError: (error: unknown, context?: string) => void;
  clearError: () => void;
  retry: (retryFn: () => Promise<void>) => Promise<void>;
}

function useErrorHandler(): UseErrorHandlerReturn {
  const [error, setError] = useState<ErrorInfo | null>(null);
  const { toast } = useToast();

  const handleError = useCallback((error: unknown, context?: string) => {
    const errorInfo = categorizeError(error);
    
    console.error(`[StoreFirstError${context ? ` - ${context}` : ''}]:`, {
      error,
      category: errorInfo.category,
      code: errorInfo.code,
      message: errorInfo.message,
    });

    setError(errorInfo);

    // Show toast notification
    toast({
      title: context ? `${context} Failed` : 'Operation Failed',
      description: errorInfo.message,
      variant: 'destructive',
      duration: 5000,
    });

    // Report to error tracking service if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        tags: {
          context: context || 'unknown',
          category: errorInfo.category,
        },
        extra: {
          code: errorInfo.code,
          retryable: errorInfo.retryable,
        },
      });
    }
  }, [toast]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retry = useCallback(async (retryFn: () => Promise<void>) => {
    if (!error?.retryable) {
      console.warn('[useErrorHandler] Cannot retry: error is not retryable');
      return;
    }

    clearError();
    try {
      await retryFn();
    } catch (err) {
      handleError(err, 'Retry');
    }
  }, [error, clearError, handleError]);

  return {
    error,
    handleError,
    clearError,
    retry,
  };
}

// Export as useStoreFirstError for consistency
export const useStoreFirstError = useErrorHandler;
